import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as L from 'leaflet';

// Leaflet defaultně hledá ikony markeru relativně k bundlu, což v Angularu nefunguje.
// Přesměrujeme na CDN unpkg s odpovídající verzí.
const iconRetinaUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png';
const iconUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png';
const shadowUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png';
const iconDefault = L.icon({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = iconDefault;

export interface IStationMapPoint {
  id_station?: number;
  name?: string;
  latitude: number;
  longitude: number;
  /** Volitelný HTML obsah popupu (priorita před name) */
  popupHtml?: string;
}

/**
 * Mapa stanic přes Leaflet + OpenStreetMap (žádný API klíč).
 * - Jedna stanice → vycentruje + zoom 15
 * - Více stanic → fitBounds přes všechny markery
 * - Volitelně markeruj polohu uživatele přes [userLocation]
 */
@Component({
  selector: 'app-station-map',
  template: `<div #mapEl [style.height]="height" class="rounded" style="width:100%;border:1px solid #dee2e6;"></div>`
})
export class StationMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('mapEl', { static: true }) mapEl: ElementRef<HTMLDivElement>;

  @Input() stations: IStationMapPoint[] = [];
  @Input() userLocation: { lat: number; lng: number } | null = null;
  @Input() height: string = '280px';
  /** Pokud true, klik na marker emituje stationClick. */
  @Input() clickableMarkers = false;

  @Output() stationClick = new EventEmitter<IStationMapPoint>();

  private map: L.Map | null = null;
  private markersLayer: L.LayerGroup | null = null;
  private userMarker: L.Marker | null = null;

  ngAfterViewInit(): void {
    this.initMap();
    this.refreshMarkers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.map && (changes['stations'] || changes['userLocation'])) {
      this.refreshMarkers();
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      // Stop pending animations/timers předtím, než zlikvidujeme DOM — jinak
      // `_onZoomTransitionEnd` doběhne s undefined `_leaflet_pos`.
      try { this.map.stop(); } catch {}
      this.map.remove();
      this.map = null;
    }
  }

  private initMap(): void {
    this.map = L.map(this.mapEl.nativeElement, {
      zoomControl: true,
      // Vypnuté animace předejdou race condition mezi setView/fitBounds a destroy.
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false
    }).setView([49.8, 15.5], 7);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(this.map);

    this.markersLayer = L.layerGroup().addTo(this.map);

    // Force-resize když je mapa v skrytém containeru (např. po *ngIf nebo tab switch)
    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private refreshMarkers(): void {
    if (!this.map || !this.markersLayer) return;

    this.markersLayer.clearLayers();
    if (this.userMarker) {
      this.map.removeLayer(this.userMarker);
      this.userMarker = null;
    }

    const points = (this.stations || []).filter(s => s && s.latitude != null && s.longitude != null);

    for (const s of points) {
      const marker = L.marker([s.latitude, s.longitude]);
      const popup = s.popupHtml || (s.name ? `<strong>${this.escape(s.name)}</strong>` : '');
      if (popup) marker.bindPopup(popup);
      if (this.clickableMarkers) {
        marker.on('click', () => this.stationClick.emit(s));
      }
      marker.addTo(this.markersLayer);
    }

    if (this.userLocation) {
      const userIcon = L.divIcon({
        className: 'user-location-marker',
        html: '<div style="background:#0d6efd;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 1px #0d6efd;"></div>',
        iconSize: [14, 14],
        iconAnchor: [7, 7]
      });
      this.userMarker = L.marker([this.userLocation.lat, this.userLocation.lng], { icon: userIcon })
        .bindPopup('Vaše poloha')
        .addTo(this.map);
    }

    // Fit bounds
    const allLatLngs: L.LatLngExpression[] = points.map(s => [s.latitude, s.longitude]);
    if (this.userLocation) allLatLngs.push([this.userLocation.lat, this.userLocation.lng]);

    if (allLatLngs.length === 1) {
      this.map.setView(allLatLngs[0] as L.LatLngTuple, 15);
    } else if (allLatLngs.length > 1) {
      this.map.fitBounds(L.latLngBounds(allLatLngs), { padding: [30, 30] });
    }

    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private escape(s: string): string {
    return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  }
}
